import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  useTriggerFormInit,
  useNodeDropdownLabels,
  buildShipmentFilterUpdate,
} from '../shared';
import { Form, Flex, Divider } from 'antd';
import type { AlertTriggerData } from '@/proto/logiblocks/logiblocks';
import useToken from '@/common/helper/useToken';
import { usePaginatedFilters } from '@/common/helper/usePaginatedFilters';
import { ALERT_TRIGGER_FILTER_CONFIGS } from '@/common/helper/filterConfigs';
import { CustomizationComponent } from '@/pages/Logiblocks/types';
import { getApplyConfig } from '@/pages/Settings/Notifications/NotificationConfig';
import { useFetchAlertThresholds, useFetchCompanyCouriers } from '@/hooks';
import { ShipmentFilterApply } from './components';
import { AddSelectableItem } from '@/components';
import ShipmentAlertThresholds from '@/pages/Shipments/PerShipmentNotification/components/ShipmentAlertThresholds';
import { getTeamsDDLOFFeature } from '@/common/utils';
import { type DropdownLabelItem } from '@/apis/logiblocksLabelsAPI';
import { ShipmentFilterFormData } from '../nodeFormTypes';

interface AlertTriggerFormData extends ShipmentFilterFormData {
  alertTypes?: string[];
}

/**
 * Customization UI for ALERT_TRIGGER nodes.
 */
export const AlertTriggerCustomizationUpdate: CustomizationComponent = ({
  nodeData,
  onChange,
  isSidePannelOpen,
  step,
  setStep,
  childAddSelectedTypesRef,
  nodeId,
  workflowId,
  isReadOnly = false,
}) => {
  const { alertThreshold, isLoading } = useFetchAlertThresholds(null);
  const [selectAllAlertTypes, setSelectAllAlertTypes] = useState(false);
  const [selectedThresholdValues, setSelectedThresholdValues] = useState({});
  const [form] = Form.useForm();
  const { getToken } = useToken();
  const [teamsList, setTeamsList] = useState([]);

  // Pre-loaded labels for paginated dropdowns (fetched once when panel opens).
  const preloadedLabels = useNodeDropdownLabels({
    isSidePannelOpen,
    workflowId,
    nodeId,
    getToken,
  });

  useEffect(() => {
    getTeamsDDLOFFeature('', 'Shipment').then((val) => {
      if (val && val.length > 0) setTeamsList(val);
    });
  }, [setTeamsList]);

  // Memoize filter configs to prevent re-renders
  const filterConfigs = useMemo(() => ALERT_TRIGGER_FILTER_CONFIGS, []);

  // Use generic paginated filters hook
  const {
    getFilterData,
    getFilterLoading,
    getFilterHasMore,
    getFilterLoadMore,
  } = usePaginatedFilters(filterConfigs, getToken);

  const handleNodeData = (
    data: AlertTriggerFormData,
    isSelectAll?: boolean
  ) => {
    if (!data || Object.keys(data).length === 0) return;
    onChange({
      alert_trigger: buildShipmentFilterUpdate(
        data,
        nodeData.alert_trigger,
        applyType,
        undefined,
        isSelectAll
      ),
    });
  };

  const handleAddSelectedTypes = useCallback(() => {
    let keys: string[] = [];
    if (Object.keys(selectedThresholdValues)?.length > 0) {
      keys = Object.keys(selectedThresholdValues);
    }
    const alertTrigger: AlertTriggerData = {
      ...nodeData.alert_trigger,
      alert_types: keys,
      select_all_alert_types: selectAllAlertTypes,
    };
    onChange({ alert_trigger: alertTrigger });
    setStep(2);
  }, [
    selectedThresholdValues,
    nodeData.alert_trigger,
    onChange,
    setStep,
    selectAllAlertTypes,
  ]);

  // Set up the ref for the parent to call handleAddSelectedTypes
  useEffect(() => {
    if (childAddSelectedTypesRef) {
      childAddSelectedTypesRef.current = handleAddSelectedTypes;
    }
  }, [childAddSelectedTypesRef, handleAddSelectedTypes]);

  const [updateAlertData, setUpdateAlertData] = useState(false);
  // When delete is triggered at step 2, persist the updated selection immediately
  useEffect(() => {
    if (updateAlertData) {
      handleAddSelectedTypes();
      setUpdateAlertData(false);
    }
  }, [updateAlertData, handleAddSelectedTypes]);

  const [applyType, setApplyType] = useState([]);
  const { couriers } = useFetchCompanyCouriers();

  /**
   * Merge helper: prepend pre-loaded label items that are not already present
   * in the paginated page-1 data.  Page data takes precedence (comes first so
   * the selector deduplication logic keeps the richer version).
   */
  const mergeWithPreloaded = useCallback(
    (pageData: unknown[], preloaded: DropdownLabelItem[]): unknown[] => {
      if (preloaded.length === 0) return pageData;
      const pageIds = new Set(
        (pageData as Array<{ id?: string | number }>).map((item) =>
          String(item.id ?? '')
        )
      );
      const extra = preloaded.filter((item) => !pageIds.has(String(item.id)));
      return [...pageData, ...extra];
    },
    []
  );

  // Memoize paginated data, enriched with pre-loaded labels for off-page values.
  // origins/destinations are paginated and pre-loaded from the API.
  // products are paginated and pre-loaded from the labels API (like origins/destinations).
  // couriers/teams use non-paginated Redux state.
  const memoizedOriginsData = useMemo(
    () => mergeWithPreloaded(getFilterData(3), preloadedLabels.origins),
    [getFilterData, mergeWithPreloaded, preloadedLabels.origins]
  );
  const memoizedProductsData = useMemo(
    () => mergeWithPreloaded(getFilterData(1), preloadedLabels.products),
    [getFilterData, mergeWithPreloaded, preloadedLabels.products]
  );
  const memoizedDestinationsData = useMemo(
    () => mergeWithPreloaded(getFilterData(4), preloadedLabels.destinations),
    [getFilterData, mergeWithPreloaded, preloadedLabels.destinations]
  );

  const applyConfig = useMemo(
    () =>
      getApplyConfig({
        teamsList,
        origins: memoizedOriginsData, // Use locations for origins
        products: memoizedProductsData,
        couriers,
        destinations: memoizedDestinationsData, // Use locations for destinations
      }),
    [
      teamsList,
      memoizedOriginsData,
      memoizedProductsData,
      couriers,
      memoizedDestinationsData,
    ]
  );

  // Initialize applyType and form fields from saved nodeData.
  const hasPatchedRef = useTriggerFormInit({
    isSidePannelOpen,
    triggerData: nodeData?.alert_trigger,
    form,
    setApplyType,
    // Wait until alertThreshold data has loaded before patching.
    extraGuard: Boolean(alertThreshold && alertThreshold.length > 0),
    onPatched: (data) => {
      // Build a map of alertType → threshold_value from the loaded thresholds.
      const alertTypesArray = Array.isArray(data.alert_types)
        ? data.alert_types
        : [];
      const alertTypesObject: Record<string, string | number> = {};
      alertTypesArray.forEach((alertType: string) => {
        alertThreshold?.forEach((threshold) => {
          threshold?.alerts?.forEach((alert) => {
            if (alert.alert_type === alertType) {
              alertTypesObject[alertType] = alert.threshold_value;
            }
          });
        });
      });
      setSelectAllAlertTypes(data?.select_all_alert_types);
      setSelectedThresholdValues(alertTypesObject);
    },
  });

  // Reset the form-init patch flag on panel close so re-opening re-initializes.
  useEffect(() => {
    if (!isSidePannelOpen) {
      hasPatchedRef.current = false;
    }
  }, [isSidePannelOpen, hasPatchedRef]);

  return (
    <>
      <Form form={form} layout='vertical'>
        <Flex vertical gap={16}>
          <Flex vertical>
            <ShipmentAlertThresholds
              shipment={null}
              selectAllAlertTypes={selectAllAlertTypes}
              setSelectAllAlertTypes={setSelectAllAlertTypes}
              step={step}
              alertThreshold={alertThreshold}
              setSelectedThresholdValues={setSelectedThresholdValues}
              selectedThresholdValues={selectedThresholdValues}
              maxHeight={'100%'}
              showThresholdInputSection={false}
              enableAllThresholds={true}
              setUpdateAlertData={setUpdateAlertData}
              isLoading={isLoading}
              disabled={isReadOnly}
            />
            {step === 2 && (
              <AddSelectableItem
                text='Select alert'
                onClick={() => setStep(3)}
              />
            )}
          </Flex>
          {step === 2 && (
            <>
              <Divider
                className={'notification-divider'}
                style={{ marginBottom: '4px' }}
              />
              <ShipmentFilterApply
                applyType={applyType}
                setApplyType={setApplyType}
                form={form}
                applyConfig={applyConfig}
                handleNodeUpdate={handleNodeData}
                isReadOnly={isReadOnly}
                origins={memoizedOriginsData}
                originsLoading={getFilterLoading(3)}
                originsHasMore={getFilterHasMore(3)}
                onLoadMoreOrigins={getFilterLoadMore(3)}
                products={memoizedProductsData}
                productsLoading={getFilterLoading(1)}
                productsHasMore={getFilterHasMore(1)}
                onLoadMoreProducts={getFilterLoadMore(1)}
                destinations={memoizedDestinationsData}
                destinationsLoading={getFilterLoading(4)}
                destinationsHasMore={getFilterHasMore(4)}
                onLoadMoreDestinations={getFilterLoadMore(4)}
              />
            </>
          )}
        </Flex>
      </Form>
    </>
  );
};
